import { UploadedImageInput } from '#app/__generated__/api.js';
import {
  type useCroppingCompanyProfileMutation,
  type useCroppingCompanyProfileMutation$data,
} from '#app/__generated__/relay/useCroppingCompanyProfileMutation.graphql.js';
import {
  type useCroppingContactMutation,
  type useCroppingContactMutation$data,
} from '#app/__generated__/relay/useCroppingContactMutation.graphql.js';
import {
  type useCroppingUserProfileMutation,
  type useCroppingUserProfileMutation$data,
} from '#app/__generated__/relay/useCroppingUserProfileMutation.graphql.js';
import { DEFAULT_TIMEOUTS } from '#app/components/Toasts/constants.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useGetCloudinarySignature } from '#app/hooks/useGetCloudinarySignature.js';
import {
  UPLOAD_IMAGE_PRESETS,
  type UploadImagePreset,
  type UploadPayload,
  useUpload,
} from '#app/hooks/useUpload.js';
import {
  CONTEXT_TYPE_AVATAR,
  CONTEXT_TYPE_BRAND_CUSTOMIZATION_LOGO,
  CONTEXT_TYPE_CONTACT,
  CONTEXT_TYPE_LOGO,
} from '../context/actionTypes.js';
import { ProfileImageCropperContext } from '../context/ProfileImageCropperContext.js';
import { type ProfileImageCropperContextTypes } from '../types.js';
// disable useContraMutation eslint error
import { graphql, useMutation } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useContext, useMemo } from 'react';

const getUploadPreset = (
  context: ProfileImageCropperContextTypes,
): UploadImagePreset => {
  switch (context) {
    case 'AVATAR':
    case 'BRAND_CUSTOMIZATION_LOGO':
    case 'CONTACT':
    case 'LOGO':
      return 'avatar';
    default:
      throw new Error(
        `Unexpected UploadImagePreset value was used: ${context}`,
      );
  }
};

export const useCropping = () => {
  const {
    context,
    croppedAreaPixels,
    imageFile,
    originalImageSize,
    resetCroppingState,
    saveProfileOnUpload,
    stopCropping,
    updateCroppedPreview,
    updateIsMutationInFlight,
    updateProcessingState,
    updateUpload,
  } = useContext(ProfileImageCropperContext);
  const toast = useToast();
  const [upload] = useUpload('image');
  const {
    createCloudinaryParameters,
    getSignedTransformation,
    validateImageNeedsTransform,
  } = useGetCloudinarySignature();

  const [updateUserProfile] = useMutation<useCroppingUserProfileMutation>(
    graphql`
      mutation useCroppingUserProfileMutation($input: UpdateUserProfileInput!) {
        updateProfile: updateUserProfile(input: $input) {
          errors {
            message
          }

          userProfile {
            avatarImage {
              id
              url
              uid
            }
          }
        }
      }
    `,
  );

  const [updateCompanyProfile] = useMutation<useCroppingCompanyProfileMutation>(
    graphql`
      mutation useCroppingCompanyProfileMutation(
        $input: UpdateOrganizationInput!
      ) {
        updateProfile: updateOrganization(input: $input) {
          errors {
            message
          }
          organization {
            logoImage {
              id
              dominantHexColor
              url
              uid
            }
          }
        }
      }
    `,
  );

  const [updateContact] = useMutation<useCroppingContactMutation>(graphql`
    mutation useCroppingContactMutation($input: UpdateContactInput!) {
      updateProfile: updateContact(input: $input) {
        errors {
          message
        }
        contact {
          privateContactMetadata {
            avatarImage {
              id
              url
              uid
            }
          }
        }
      }
    }
  `);

  const toastMessages = useMemo(
    () => ({
      genericError: 'An error occurred. Please try again.',
      missingImage: {
        [CONTEXT_TYPE_AVATAR]: 'Profile photo image is required.',
        [CONTEXT_TYPE_BRAND_CUSTOMIZATION_LOGO]: 'Logo image is required.',
        [CONTEXT_TYPE_CONTACT]: 'Contact photo image is required.',
        [CONTEXT_TYPE_LOGO]: 'Logo image is required.',
      },
      success: {
        [CONTEXT_TYPE_AVATAR]: 'Profile photo updated.',
        [CONTEXT_TYPE_BRAND_CUSTOMIZATION_LOGO]: 'Custom branding updated.',
        [CONTEXT_TYPE_CONTACT]: 'Contact photo updated.',
        [CONTEXT_TYPE_LOGO]: 'Company logo updated.',
      },
    }),
    [],
  );

  const onMutationCompleted = useCallback(
    (
      data:
        | useCroppingCompanyProfileMutation$data
        | useCroppingContactMutation$data
        | useCroppingUserProfileMutation$data,
    ) => {
      if (data.updateProfile.errors?.[0]) {
        resetCroppingState();
        toast.error(data.updateProfile.errors[0].message);
      } else {
        toast.success(toastMessages.success[context], {
          duration: DEFAULT_TIMEOUTS.success,
        });
        stopCropping();
      }
    },
    [resetCroppingState, toast, toastMessages.success, context, stopCropping],
  );

  const getResolvedInput = useCallback(
    ({
      companyId,
      contactId,
      uploadedImage,
    }: {
      companyId?: string;
      contactId?: string;
      uploadedImage: UploadPayload;
    }) => {
      const onCompleted = onMutationCompleted;

      const contraImage: UploadedImageInput = {
        animated: uploadedImage.animated,
        checksum: uploadedImage.checksum,
        height: uploadedImage.height,
        originalFormat: uploadedImage.originalFormat,
        sizeBytes: uploadedImage.sizeBytes,
        uid: uploadedImage.uid,
        width: uploadedImage.width,
      };

      switch (context) {
        case 'AVATAR':
          return {
            updateProfile: () => {
              updateUserProfile({
                onCompleted,
                variables: { input: { avatarImage: contraImage } },
              });
            },
          };

        case 'BRAND_CUSTOMIZATION_LOGO':
          return {
            updateProfile: () => null,
          };

        case 'CONTACT':
          if (contactId) {
            return {
              updateProfile: () => {
                updateContact({
                  onCompleted,
                  variables: {
                    input: {
                      avatarImage: contraImage,
                      contactId,
                    },
                  },
                });
              },
            };
          } else {
            throw new Error(
              'Contact avatar cannot be altered without a contact id',
            );
          }

        case 'LOGO':
          if (companyId) {
            return {
              updateProfile: () => {
                updateCompanyProfile({
                  onCompleted,
                  variables: {
                    input: {
                      id: companyId,
                      logoImage: contraImage,
                    },
                  },
                });
              },
            };
          } else {
            throw new Error(
              'Company logo cannot be altered without an organization id',
            );
          }

        default:
          throw new Error(`Unhandled image cropping type: ${context}`);
      }
    },
    [
      context,
      onMutationCompleted,
      updateCompanyProfile,
      updateContact,
      updateUserProfile,
    ],
  );

  const uploadFromSource = useCallback(
    ({
      contactId,
      cropArea,
      file,
      organizationId,
      signatureOptions,
    }: Pick<
      Parameters<typeof upload>[0],
      'cropArea' | 'file' | 'signatureOptions'
    > & {
      contactId?: string;
      organizationId?: string;
    }) => {
      updateIsMutationInFlight(true);

      upload({
        cropArea,
        file,
        onCompleted: (uploadedImage, preview) => {
          updateUpload({
            animated: uploadedImage.animated,
            checksum: uploadedImage.checksum,
            height: uploadedImage.height,
            originalFormat: uploadedImage.originalFormat,
            sizeBytes: uploadedImage.sizeBytes,
            uid: uploadedImage.uid,
            width: uploadedImage.width,
          });
          updateCroppedPreview(preview);

          const resolvedInput = getResolvedInput({
            companyId: organizationId,
            contactId,
            uploadedImage,
          });

          if (saveProfileOnUpload && resolvedInput) {
            resolvedInput.updateProfile();
          }

          stopCropping();
        },
        onError: (error) => {
          toast.error(toastMessages.genericError);
          captureException(error);
          stopCropping();
        },
        preset: getUploadPreset(context),
        signatureOptions,
      });
    },
    [
      context,
      getResolvedInput,
      saveProfileOnUpload,
      stopCropping,
      updateIsMutationInFlight,
      toast,
      toastMessages.genericError,
      updateCroppedPreview,
      updateUpload,
      upload,
    ],
  );

  const onCrop = useCallback(
    ({
      contactId,
      organizationId,
    }: {
      contactId?: string;
      organizationId?: string;
    }) => {
      const pixelsToSave = croppedAreaPixels[context];
      const originalSize = originalImageSize[context];
      if (imageFile && pixelsToSave && originalSize) {
        updateProcessingState(true);
        if (
          validateImageNeedsTransform({
            croppedHeight: pixelsToSave.height,
            croppedWidth: pixelsToSave.width,
            originalHeight: originalSize.height,
            originalWidth: originalSize.width,
            x: pixelsToSave.x,
            y: pixelsToSave.y,
          })
        ) {
          const cloudinaryTransforms = createCloudinaryParameters({
            croppedAreaData: pixelsToSave,
            mediaSize: originalSize,
          });

          getSignedTransformation({
            input: {
              transformation: cloudinaryTransforms,
              uploadPreset: UPLOAD_IMAGE_PRESETS[getUploadPreset(context)].name,
            },
            uploadCallback: (signatureOptions) => {
              uploadFromSource({
                contactId,
                file: imageFile,
                organizationId,
                signatureOptions,
              });
            },
          });
        } else {
          uploadFromSource({
            contactId,
            cropArea: pixelsToSave,
            file: imageFile,
            organizationId,
          });
        }
      } else {
        toast.error(toastMessages.missingImage[context]);
      }
    },
    [
      croppedAreaPixels,
      context,
      originalImageSize,
      imageFile,
      updateProcessingState,
      validateImageNeedsTransform,
      createCloudinaryParameters,
      getSignedTransformation,
      uploadFromSource,
      toast,
      toastMessages.missingImage,
    ],
  );

  return { onCrop };
};
